"use client"

import {
  LayoutDashboard, Users, Calendar, TrendingUp,
  Megaphone, Settings, FlaskConical, Package, BookOpen, X, Receipt,
  FileBarChart, Menu, LogOut,
} from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useMemo, useState } from "react"
import { useAuth } from "@/contexts/auth-context"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import type { MeOut } from "@/lib/api/me"
import { cn } from "@/lib/utils"
import { UserDropdown } from "./user-dropdown"

const MODULOS_DEFAULT: Record<string, string[]> = {
  admin:       ["dashboard","pacientes","agenda","presupuestos","finanzas","reportes","laboratorios","insumos","catalogo","marketing","configuracion"],
  profesional: ["dashboard","pacientes","agenda","presupuestos","finanzas","reportes","laboratorios","insumos","catalogo"],
  recepcion:   ["dashboard","pacientes","agenda","presupuestos"],
  marketing:   ["dashboard","pacientes","insumos","catalogo","marketing"],
}

const NAV_GROUPS = [
  {
    label: "Principal",
    items: [
      { name: "Dashboard",    href: "/",             icon: LayoutDashboard, modulo: "dashboard" },
      { name: "Pacientes",    href: "/pacientes",    icon: Users,           modulo: "pacientes" },
      { name: "Agenda",       href: "/agenda",       icon: Calendar,        modulo: "agenda" },
      { name: "Presupuestos", href: "/presupuestos", icon: Receipt,         modulo: "presupuestos" },
    ],
  },
  {
    label: "Clínica",
    items: [
      { name: "Finanzas",     href: "/finanzas",     icon: TrendingUp,      modulo: "finanzas" },
      { name: "Reportes",     href: "/reportes",     icon: FileBarChart,    modulo: "reportes" },
      { name: "Laboratorios", href: "/laboratorios", icon: FlaskConical,    modulo: "laboratorios" },
      { name: "Insumos",      href: "/insumos",      icon: Package,         modulo: "insumos" },
      { name: "Catálogo",     href: "/catalogo",     icon: BookOpen,        modulo: "catalogo" },
    ],
  },
  {
    label: "Otros",
    items: [
      { name: "Marketing",     href: "/marketing",     icon: Megaphone, modulo: "marketing" },
      { name: "Configuración", href: "/configuracion", icon: Settings,  modulo: "configuracion" },
    ],
  },
]

// Sub-navegación de Marketing (se muestra cuando se está en una ruta de /marketing)
// Vacía a propósito (2026-09-25, Julio): el ítem "Comunicaciones" era redundante
// con "Marketing" en la nav principal. Las secciones (Enviar/Historial/Plantillas/
// Automatizaciones/Listas) se manejan con las tabs dentro de /marketing.
const MARKETING_SUB_NAV: { name: string; href: string }[] = []

const bottomNavConfig = [
  { name: "Dashboard",    href: "/",             icon: LayoutDashboard, modulo: "dashboard" },
  { name: "Pacientes",    href: "/pacientes",    icon: Users,           modulo: "pacientes" },
  { name: "Agenda",       href: "/agenda",       icon: Calendar,        modulo: "agenda" },
  { name: "Presupuestos", href: "/presupuestos", icon: Receipt,         modulo: "presupuestos" },
]

// Módulos que aparecen en el Sheet "Más" (los que no caben en BottomNav)
const moreNavConfig = [
  { name: "Finanzas",      href: "/finanzas",      icon: TrendingUp,   modulo: "finanzas" },
  { name: "Reportes",      href: "/reportes",      icon: FileBarChart, modulo: "reportes" },
  { name: "Laboratorios",  href: "/laboratorios",  icon: FlaskConical, modulo: "laboratorios" },
  { name: "Insumos",       href: "/insumos",       icon: Package,      modulo: "insumos" },
  { name: "Catálogo",      href: "/catalogo",      icon: BookOpen,     modulo: "catalogo" },
  { name: "Marketing",     href: "/marketing",     icon: Megaphone,    modulo: "marketing" },
  { name: "Configuración", href: "/configuracion", icon: Settings,     modulo: "configuracion" },
]

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
  onEditProfile?: () => void
  meData?: MeOut
}

export function Sidebar({ isOpen = false, onClose, onEditProfile, meData }: SidebarProps) {
  const pathname = usePathname()
  const { user } = useAuth()

  const allowedModulos = useMemo(() => {
    if (!user) return null
    return meData?.modulos_habilitados ?? MODULOS_DEFAULT[user.rol] ?? []
  }, [user?.rol, meData?.modulos_habilitados])

  const groups = useMemo(
    () => allowedModulos
      ? NAV_GROUPS
          .map(g => ({ label: g.label, items: g.items.filter(i => allowedModulos.includes(i.modulo)) }))
          .filter(g => g.items.length > 0)
      : NAV_GROUPS,
    [allowedModulos]
  )

  // Determinar si estamos en una ruta de marketing
  const isMarketingSection = pathname === '/marketing' || pathname?.startsWith('/marketing/')
  const marketingSubItems = useMemo(() => {
    if (!isMarketingSection || !allowedModulos?.includes('marketing')) return []
    return MARKETING_SUB_NAV.filter(item => {
      // Solo mostrar si el usuario tiene acceso al módulo marketing
      return true
    })
  }, [isMarketingSection, allowedModulos])

  return (
    <aside className={cn(
      "fixed left-0 top-0 h-screen w-56 border-r border-sidebar-border bg-sidebar z-50 flex flex-col",
      "transition-transform duration-300 ease-in-out will-change-transform",
      "md:translate-x-0",
      isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
    )}>

      {/* Logo */}
      <div className="px-6 pt-6 pb-5 flex items-baseline justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-[16px] font-bold tracking-tight text-sidebar-foreground uppercase">Aden</span>
          <span className="text-[10px] text-sidebar-foreground/40 font-medium tracking-wider uppercase">Crm</span>
        </div>
        <button
          onClick={onClose}
          aria-label="Cerrar menú"
          className="md:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      {/* Nav con grupos */}
      <nav className="flex-1 px-3 pb-2 overflow-y-auto scrollbar-hide space-y-3" aria-label="Navegación principal">
        {groups.map((group, gi) => (
          <div key={gi}>
            <p className="px-3 mb-0.5 text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/30 select-none">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href))
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    onClick={onClose}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] border transition-colors duration-150",
                      "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-foreground font-semibold border-sidebar-border'
                        : 'border-transparent text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                    )}
                  >
                    <item.icon
                      className={cn("size-[15px] shrink-0", isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/40')}
                      strokeWidth={isActive ? 2 : 1.5}
                      aria-hidden
                    />
                    {item.name}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}

        {/* Sub-menú de Marketing (dentro del navegación principal) */}
        {marketingSubItems.length > 0 && (
          <div className="space-y-0.5 pl-3 border-l-2 border-sidebar-border/30">
            {marketingSubItems.map((item) => {
              const isActive = pathname === item.href || (item.href !== '/marketing' && pathname.startsWith(item.href))
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={onClose}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] transition-colors duration-150",
                    "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-foreground font-semibold'
                      : 'text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                  )}
                >
                  {item.name}
                </Link>
              )
            })}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4 pt-2 border-t border-sidebar-border">
        <UserDropdown
          fotoUrl={meData?.foto_url}
          nombreOverride={meData?.nombre}
          onEditProfile={onEditProfile ?? (() => {})}
        />
      </div>
    </aside>
  )
}

// ── BottomNav ─────────────────────────────────────────────────────────────────

export function BottomNav() {
  const pathname = usePathname()
  const { user } = useAuth()
  const { logout } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)

  const items = useMemo(
    () => user
      ? bottomNavConfig.filter(i => (MODULOS_DEFAULT[user.rol] ?? []).includes(i.modulo))
      : bottomNavConfig,
    [user?.rol]
  )

  const moreItems = useMemo(
    () => user
      ? moreNavConfig.filter(i => (MODULOS_DEFAULT[user.rol] ?? []).includes(i.modulo))
      : moreNavConfig,
    [user?.rol]
  )

  // Resaltar "Más" si la ruta activa está en uno de esos módulos
  const moreIsActive = moreNavConfig.some(
    i => pathname === i.href || (i.href !== '/' && pathname.startsWith(i.href))
  )

  return (
    <>
      <nav
        aria-label="Navegación móvil"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border"
      >
        <ul className="flex items-stretch" role="list">
          {items.map((item) => {
            const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href))
            return (
              <li key={item.href} className="flex-1">
                <Link
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.name}
                  className={cn(
                    "flex flex-col items-center justify-center gap-0.5 min-h-[56px] w-full px-1 py-2",
                    "text-[10px] font-medium transition-colors duration-150",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <item.icon
                    className={cn("size-5 shrink-0", isActive ? "text-primary" : "text-muted-foreground")}
                    strokeWidth={isActive ? 2.2 : 1.5}
                    aria-hidden
                  />
                  <span className="truncate max-w-full leading-tight">{item.name}</span>
                </Link>
              </li>
            )
          })}

          {/* Botón Más */}
          {moreItems.length > 0 && (
            <li className="flex-1">
              <button
                onClick={() => setMoreOpen(true)}
                aria-label="Más módulos"
                aria-expanded={moreOpen}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 min-h-[56px] w-full px-1 py-2",
                  "text-[10px] font-medium transition-colors duration-150",
                  moreIsActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Menu
                  className={cn("size-5 shrink-0", moreIsActive ? "text-primary" : "text-muted-foreground")}
                  strokeWidth={moreIsActive ? 2.2 : 1.5}
                  aria-hidden
                />
                <span className="truncate max-w-full leading-tight">Menú</span>
              </button>
            </li>
          )}
        </ul>
      </nav>

      {/* Sheet de módulos adicionales */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-xl pb-safe">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-sm font-semibold">Más módulos</SheetTitle>
          </SheetHeader>
          <nav aria-label="Módulos adicionales">
            <ul className="grid grid-cols-3 gap-2">
              {moreItems.map((item) => {
                const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href))
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setMoreOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        "flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl min-h-[72px]",
                        "text-[11px] font-medium transition-colors duration-150 border",
                        isActive
                          ? "bg-primary/10 text-primary border-primary/20"
                          : "bg-muted text-muted-foreground hover:text-foreground hover:bg-accent border-transparent"
                      )}
                    >
                      <item.icon
                        className={cn("size-5 shrink-0", isActive ? "text-primary" : "text-muted-foreground")}
                        strokeWidth={isActive ? 2.2 : 1.5}
                        aria-hidden
                      />
                      {item.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>
          {/* Cerrar sesión */}
          <div className="mt-4 pt-3 border-t border-border">
            <button
              onClick={() => { setMoreOpen(false); logout() }}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors min-h-[44px]"
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              Cerrar sesión
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
